import styled from "@emotion/styled";
import React from "react";

import { Container } from "@brexhq/next/src/components/layout/Container/Container";
import { getModuleThemeColors } from "@brexhq/next/src/components/pageBuilder/ModuleLayout";
import { BreakPointVals } from "@brexhq/shared/styles/breakpoints";
import {
  CYAN_LIGHT,
  ModuleThemeType,
  PEACH_LIGHT,
  CYAN_DARK,
  PEACH_DARK,
  isDarkTheme,
} from "@brexhq/shared/types/ModuleTheme";
import { SanityIconPickerProps } from "@brexhq/shared/types/modules/BlockRowsModule/BlockRowItems";
import {
  iconSizes,
  IconSizes,
} from "@brexhq/shared/types/modules/BlockRowsModule/BlockRowItems/IconPicker";

export const GRADIENT_LIGHT_DEFINITION_SVG_ID = "peach-cyan-gradient-light";
export const GRADIENT_DARK_DEFINITION_SVG_ID = "peach-cyan-gradient-dark";
export const GradientDefinitionSVG = () => (
  // This SVG doesn't actually render anything, just stores a global definition for the gradient that other svgs can reference.
  <svg
    width="0"
    height="0"
    viewBox="0 0 0 0"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: "block" }}
  >
    <defs>
      <linearGradient
        id={GRADIENT_LIGHT_DEFINITION_SVG_ID}
        x1="0%"
        y1="0%"
        x2="100%"
        y2="100%"
      >
        <stop offset="0%" stopColor={PEACH_LIGHT} />
        <stop offset="100%" stopColor={CYAN_LIGHT} />
      </linearGradient>
      <linearGradient
        id={GRADIENT_DARK_DEFINITION_SVG_ID}
        x1="0%"
        y1="0%"
        x2="100%"
        y2="100%"
      >
        <stop offset="0%" stopColor={CYAN_DARK} />
        <stop offset="100%" stopColor={PEACH_DARK} />
      </linearGradient>
    </defs>
  </svg>
);

export const SVGWrapper = styled.div<{
  isGradient: boolean;
  gradientVariation: "light" | "dark";
}>(({ isGradient, gradientVariation }) => ({
  ["& > svg"]: {
    width: "100%",
    height: "auto",

    "> path": {
      // Reference the SVG that contains the gradient definition
      ...(isGradient && {
        fill: `url(#${
          gradientVariation === "light"
            ? GRADIENT_LIGHT_DEFINITION_SVG_ID
            : GRADIENT_DARK_DEFINITION_SVG_ID
        })`,
      }),
    },
  },
}));

interface Props extends Omit<SanityIconPickerProps, "_key" | "_type" | "size"> {
  theme: ModuleThemeType;
  size: IconSizes | Record<BreakPointVals, IconSizes>;
  "data-testid"?: string;
}

export const IconPicker = ({
  marginTop,
  icon,
  color,
  size,
  theme,
  "data-testid": testId,
}: Props) => {
  const {
    standard: { icons },
  } = getModuleThemeColors(theme);

  const isGradient = color === "gradientPeachCyan";
  const gradientVariation = isDarkTheme(theme) ? "light" : "dark";
  const isMultiColor = color === "multi";
  const svg = isMultiColor ? icon.color : icon.mono;

  // Could be the case if user hasn't added a color or mono icon but then selected that option in sanity
  if (!svg) return null;

  return (
    <Container
      margin={[marginTop, 0, 0, 0]}
      width={{
        LRG: iconSizes[typeof size === "string" ? size : size.LRG],
        MED: iconSizes[typeof size === "string" ? size : size.MED],
        SML: iconSizes[typeof size === "string" ? size : size.SML],
      }}
      textColor={isMultiColor ? undefined : icons}
      data-testid={testId}
    >
      <SVGWrapper
        isGradient={isGradient}
        gradientVariation={gradientVariation}
        dangerouslySetInnerHTML={{ __html: svg }}
      />
    </Container>
  );
};
